import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Form,
  Image,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  addCatalogItem,
  assignCatalogToAccount,
  deleteCatalog,
  getCatalog,
  listContractorAccounts,
  removeCatalogItem,
  setCatalogPublishSettings,
} from '../../services/contractorsHubApi';
import { getOrderGenieErpPricesBulk } from '../../services/orderXApi';
import SearchProductMasterModal from './SearchProductMasterModal';
import { useProductImageBaseUrl } from './useProductImageBaseUrl';
import { buildProductImageUrl } from '../../pages/orderXV4Utils';

const { Text } = Typography;

const FSM_PLATFORM_LABELS = {
  service_titan: 'ServiceTitan',
};

const fsmPlatformLabel = (platform) => FSM_PLATFORM_LABELS[platform] || platform;

const PublishSettingsTab = ({ masterId, fsmPlatform, initialValues, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    form.setFieldsValue({
      categories: (initialValues.categories || []).join(', '),
      active: initialValues.active !== false,
      taxable: Boolean(initialValues.taxable),
      account: initialValues.account || '',
      costOfSaleAccount: initialValues.costOfSaleAccount || '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fsmPlatform, initialValues]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const categories = String(values.categories || '')
        .split(',')
        .map((v) => parseInt(v.trim(), 10))
        .filter((v) => Number.isFinite(v));
      await setCatalogPublishSettings(
        masterId,
        {
          categories,
          active: Boolean(values.active),
          taxable: Boolean(values.taxable),
          account: values.account || '',
          costOfSaleAccount: values.costOfSaleAccount || '',
        },
        fsmPlatform,
      );
      message.success('Publish settings saved');
      onSaved();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message || 'Could not save publish settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form form={form} layout="vertical" style={{ marginTop: 8, maxWidth: 480 }}>
      <Form.Item name="categories" label="Category IDs (comma-separated)">
        <Input placeholder="e.g. 567943432, 123456" />
      </Form.Item>
      <Space size="large">
        <Form.Item name="active" label="Active" valuePropName="checked" style={{ marginBottom: 0 }}>
          <Switch />
        </Form.Item>
        <Form.Item name="taxable" label="Taxable" valuePropName="checked" style={{ marginBottom: 0 }}>
          <Switch />
        </Form.Item>
      </Space>
      <Form.Item name="account" label="Account" style={{ marginTop: 16 }}>
        <Input placeholder="e.g. Income" />
      </Form.Item>
      <Form.Item name="costOfSaleAccount" label="Cost of Sale Account">
        <Input placeholder="e.g. Cost of Goods Sold:Field Material & Supply Expense" />
      </Form.Item>
      <Button onClick={handleSave} loading={saving}>
        Save Publish Settings
      </Button>
    </Form>
  );
};

const CatalogDetailModal = ({ masterId, onClose, onChanged }) => {
  const [catalog, setCatalog] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [addForm] = Form.useForm();
  const [adding, setAdding] = useState(false);
  const [removingSku, setRemovingSku] = useState('');
  const [contractorAccounts, setContractorAccounts] = useState([]);
  const [selectedContractor, setSelectedContractor] = useState(undefined);
  const [assigning, setAssigning] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [priceEdits, setPriceEdits] = useState({});
  const [savingSku, setSavingSku] = useState('');
  const [pricingContractorId, setPricingContractorId] = useState(undefined);
  const [erpPrices, setErpPrices] = useState(null);
  const [erpPricesLoading, setErpPricesLoading] = useState(false);
  const [erpPricesError, setErpPricesError] = useState('');
  const imageBaseUrl = useProductImageBaseUrl();

  const load = useCallback(async () => {
    if (!masterId) return;
    setLoading(true);
    try {
      const [catalogData, accounts] = await Promise.all([getCatalog(masterId), listContractorAccounts()]);
      setCatalog(catalogData);
      setContractorAccounts(accounts);
      const nextPriceEdits = {};
      (catalogData.items || []).forEach((item) => {
        nextPriceEdits[item.sku] = { list_price: item.list_price, customer_price: item.customer_price };
      });
      setPriceEdits(nextPriceEdits);
      // One associated contractor: price as that contractor automatically.
      // Several: keep the current pick if it's still associated, otherwise
      // wait for the user to choose which contractor to price as.
      const associated = catalogData.associated_contractors || [];
      setPricingContractorId((prev) => {
        if (associated.length === 1) return associated[0].id;
        return associated.some((c) => c.id === prev) ? prev : undefined;
      });
      setError('');
    } catch (err) {
      setError(err.message || 'Could not load catalog');
    } finally {
      setLoading(false);
    }
  }, [masterId]);

  useEffect(() => {
    load();
  }, [load]);

  // Live ERP price per sku for the selected contractor, via the same
  // connector pricing call OrderX uses, keyed by the contractor's ERP id.
  const pricingErpCustomerId = contractorAccounts.find((a) => a.id === pricingContractorId)?.p21_customer_id;
  const catalogSkusKey = (catalog?.items || []).map((item) => item.sku).join('\n');
  useEffect(() => {
    const skus = catalogSkusKey ? catalogSkusKey.split('\n').filter(Boolean) : [];
    if (!pricingErpCustomerId || !skus.length) {
      setErpPrices(null);
      setErpPricesError('');
      return undefined;
    }
    let cancelled = false;
    setErpPricesLoading(true);
    getOrderGenieErpPricesBulk(skus, { customer_id_erp: pricingErpCustomerId })
      .then((resp) => {
        if (cancelled) return;
        setErpPrices(resp?.prices && typeof resp.prices === 'object' ? resp.prices : {});
        setErpPricesError('');
      })
      .catch((err) => {
        if (cancelled) return;
        setErpPrices(null);
        setErpPricesError(err.message || 'Could not load ERP prices');
      })
      .finally(() => {
        if (!cancelled) setErpPricesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pricingErpCustomerId, catalogSkusKey]);

  const handleChanged = () => {
    load();
    onChanged();
  };

  const handleAddItem = async () => {
    try {
      const values = await addForm.validateFields();
      setAdding(true);
      await addCatalogItem(masterId, {
        sku: values.sku,
        name: values.name,
        listPrice: Number(values.listPrice),
        customerPrice: Number(values.customerPrice) || 0,
        description: values.description,
        imageUrl: values.imageUrl,
      });
      message.success('Item added');
      addForm.resetFields();
      handleChanged();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message || 'Could not add item');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveItem = async (sku) => {
    setRemovingSku(sku);
    try {
      await removeCatalogItem(masterId, sku);
      message.success('Item removed');
      handleChanged();
    } catch (err) {
      message.error(err.message || 'Could not remove item');
    } finally {
      setRemovingSku('');
    }
  };

  const handlePriceFieldChange = (sku, field, value) => {
    setPriceEdits((prev) => ({ ...prev, [sku]: { ...prev[sku], [field]: value } }));
  };

  const handleSavePrice = async (row) => {
    setSavingSku(row.sku);
    try {
      const edited = priceEdits[row.sku] || {};
      await addCatalogItem(masterId, {
        sku: row.sku,
        name: row.name,
        listPrice: Number(edited.list_price) || 0,
        customerPrice: Number(edited.customer_price) || 0,
        description: row.description,
        imageUrl: row.image_url,
      });
      message.success('Price updated');
      handleChanged();
    } catch (err) {
      message.error(err.message || 'Could not update price');
    } finally {
      setSavingSku('');
    }
  };

  const handleAssignContractor = async () => {
    setAssigning(true);
    try {
      await assignCatalogToAccount(selectedContractor, masterId);
      message.success('Catalog associated with contractor account');
      setSelectedContractor(undefined);
      handleChanged();
    } catch (err) {
      message.error(err.message || 'Could not associate catalog with contractor account');
    } finally {
      setAssigning(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteCatalog(masterId);
      message.success('Catalog deleted');
      onChanged();
      onClose();
    } catch (err) {
      message.error(err.message || 'Could not delete catalog');
    } finally {
      setDeleting(false);
    }
  };

  const priceChanged = (row) =>
    Number(priceEdits[row.sku]?.list_price) !== Number(row.list_price || 0) ||
    Number(priceEdits[row.sku]?.customer_price) !== Number(row.customer_price || 0);

  const associatedContractors = catalog?.associated_contractors || [];

  const erpPriceControl =
    associatedContractors.length > 1 ? (
      <Space>
        <Text type="secondary">ERP price for</Text>
        <Select
          style={{ width: 260 }}
          placeholder="Select contractor to display price"
          value={pricingContractorId}
          onChange={setPricingContractorId}
          options={associatedContractors.map((c) => ({ value: c.id, label: c.display_name }))}
        />
      </Space>
    ) : associatedContractors.length === 1 ? (
      <Text type="secondary">
        ERP price for <Text strong>{associatedContractors[0].display_name}</Text>
      </Text>
    ) : null;

  const renderErpPrice = (_, row) => {
    if (!pricingContractorId) {
      return <Text type="secondary">{associatedContractors.length ? 'Select contractor' : '—'}</Text>;
    }
    if (erpPricesLoading) return <Spin size="small" />;
    if (!erpPrices) return <Text type="secondary">—</Text>;
    const price = erpPrices[row.sku];
    return price === undefined ? <Tag>Not priced</Tag> : `$${Number(price).toFixed(2)}`;
  };

  const itemsTab = (
    <div style={{ height: '100%', overflow: 'auto', padding: '16px 24px' }}>
      <Space style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: 12 }}>
        <Text strong>
          Items ({catalog ? catalog.items.length : 0}){loading ? ' · refreshing…' : ''}
        </Text>
        <Space>
          {erpPriceControl}
          <Button onClick={() => setSearchOpen(true)}>Add from Product Master</Button>
        </Space>
      </Space>
      {erpPricesError && <Alert type="warning" showIcon message={erpPricesError} style={{ marginBottom: 12 }} />}
      <Table
        rowKey="sku"
        loading={loading}
        dataSource={catalog ? catalog.items : []}
        pagination={{ pageSize: 20 }}
        locale={{ emptyText: <Empty description="No items yet" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        columns={[
          {
            title: 'Image',
            dataIndex: 'image_url',
            width: 64,
            render: (url) => {
              const resolved = buildProductImageUrl(url, imageBaseUrl);
              return resolved ? (
                <Image src={resolved} alt="" width={40} height={40} style={{ objectFit: 'contain' }} />
              ) : (
                <div style={{ width: 40, height: 40, background: '#f0f0f0', borderRadius: 4 }} />
              );
            },
          },
          { title: 'SKU', dataIndex: 'sku', width: 160 },
          { title: 'Name', dataIndex: 'name' },
          {
            title: 'List Price',
            dataIndex: 'list_price',
            width: 150,
            render: (_, row) => (
              <InputNumber
                min={0}
                step={0.01}
                value={priceEdits[row.sku]?.list_price}
                onChange={(value) => handlePriceFieldChange(row.sku, 'list_price', value)}
                style={{ width: 130 }}
              />
            ),
          },
          {
            title: 'Customer Price',
            dataIndex: 'customer_price',
            width: 150,
            render: (_, row) => (
              <InputNumber
                min={0}
                step={0.01}
                value={priceEdits[row.sku]?.customer_price}
                onChange={(value) => handlePriceFieldChange(row.sku, 'customer_price', value)}
                style={{ width: 130 }}
              />
            ),
          },
          {
            title: 'ERP Price',
            key: 'erp_price',
            width: 130,
            render: renderErpPrice,
          },
          {
            title: '',
            key: 'actions',
            width: 180,
            render: (_, row) => (
              <Space>
                <Button
                  size="small"
                  onClick={() => handleSavePrice(row)}
                  loading={savingSku === row.sku}
                  disabled={!priceChanged(row)}
                >
                  Save Price
                </Button>
                <Popconfirm title={`Remove ${row.sku}?`} onConfirm={() => handleRemoveItem(row.sku)}>
                  <Button type="link" danger size="small" loading={removingSku === row.sku}>
                    Remove
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
    </div>
  );

  const settingsTab = (
    <div style={{ height: '100%', overflow: 'auto', padding: '16px 24px' }}>
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        Publish Settings
      </Text>
      <Text type="secondary">
        Publish settings are specific to each data pipe -- pick a pipe below to view or edit the values used
        when this catalog is published there. Shown is what's currently in effect: this catalog's own saved
        settings for that pipe, or (if it's never saved any) the tenant-wide default from the Data Pipes tab.
        Saving here always overrides the tenant default for this catalog specifically.
      </Text>
      <Tabs
        style={{ marginTop: 8 }}
        items={(catalog?.available_fsm_platforms || []).map((platform) => ({
          key: platform,
          label: fsmPlatformLabel(platform),
          children: (
            <PublishSettingsTab
              masterId={masterId}
              fsmPlatform={platform}
              initialValues={catalog?.effective_publish_settings?.[platform] || {}}
              onSaved={handleChanged}
            />
          ),
        }))}
      />

      <Text strong style={{ display: 'block', marginTop: 32, marginBottom: 8 }}>
        Add Item Manually
      </Text>
      <Form form={addForm} layout="vertical" style={{ maxWidth: 480 }}>
        <Form.Item name="sku" rules={[{ required: true, message: 'SKU required' }]}>
          <Input placeholder="SKU" />
        </Form.Item>
        <Form.Item name="name" rules={[{ required: true, message: 'Name required' }]}>
          <Input placeholder="Item name" />
        </Form.Item>
        <Space style={{ width: '100%' }}>
          <Form.Item name="listPrice" rules={[{ required: true, message: 'List price required' }]}>
            <InputNumber placeholder="List Price" min={0} step={0.01} style={{ width: 220 }} />
          </Form.Item>
          <Form.Item name="customerPrice">
            <InputNumber placeholder="Customer Price" min={0} step={0.01} style={{ width: 220 }} />
          </Form.Item>
        </Space>
        <Form.Item name="description">
          <Input placeholder="Description (optional)" />
        </Form.Item>
        <Form.Item name="imageUrl">
          <Input placeholder="Image URL (optional)" />
        </Form.Item>
        <Form.Item>
          <Button type="primary" onClick={handleAddItem} loading={adding}>
            Add
          </Button>
        </Form.Item>
      </Form>
    </div>
  );

  return (
    <Drawer
      title={catalog ? catalog.display_name : 'Catalog'}
      open={Boolean(masterId)}
      onClose={onClose}
      placement="right"
      width="100%"
      destroyOnClose
      styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
      footer={
        <Space style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <Button onClick={onClose}>Close</Button>
        </Space>
      }
    >
      {error && <Alert type="error" message={error} style={{ margin: 16 }} />}
      {catalog && (
        <>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
            <Space style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <Text type="secondary">
                Catalog Key: <Text code>{catalog.catalog_key}</Text> · Version {catalog.version_number || '—'}
              </Text>
              <Popconfirm
                title="Delete this catalog?"
                description="This cannot be undone. It must not be assigned to any contractor account."
                onConfirm={handleDelete}
              >
                <Button danger loading={deleting}>
                  Delete Catalog
                </Button>
              </Popconfirm>
            </Space>

            <Text strong style={{ display: 'block', marginTop: 16, marginBottom: 4 }}>
              Associated Contractor(s)
            </Text>
            {catalog.associated_contractors && catalog.associated_contractors.length > 0 ? (
              <Space wrap style={{ marginBottom: 8 }}>
                {catalog.associated_contractors.map((c) => (
                  <Tag key={c.id} color="blue">
                    {c.display_name}
                  </Tag>
                ))}
              </Space>
            ) : (
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                Not associated with any contractor account yet.
              </Text>
            )}
            <Space>
              <Select
                style={{ width: 300 }}
                placeholder="Select a contractor account"
                value={selectedContractor}
                onChange={setSelectedContractor}
                options={contractorAccounts.map((a) => ({ value: a.id, label: a.display_name }))}
              />
              <Button onClick={handleAssignContractor} loading={assigning} disabled={!selectedContractor}>
                Associate with Contractor
              </Button>
            </Space>
          </div>

          <style>{`
            .catalog-detail-tabs { flex: 1; min-height: 0; display: flex; flex-direction: column; }
            .catalog-detail-tabs .ant-tabs-nav { flex-shrink: 0; margin: 0 24px; }
            .catalog-detail-tabs .ant-tabs-content-holder { flex: 1; min-height: 0; }
            .catalog-detail-tabs .ant-tabs-content { height: 100%; }
            .catalog-detail-tabs .ant-tabs-tabpane { height: 100%; }
          `}</style>
          <Tabs
            defaultActiveKey="items"
            className="catalog-detail-tabs"
            items={[
              { key: 'items', label: `Items (${catalog.items.length})`, children: itemsTab },
              { key: 'settings', label: 'Settings', children: settingsTab },
            ]}
          />
        </>
      )}
      <SearchProductMasterModal
        open={searchOpen}
        masterId={masterId}
        onClose={() => setSearchOpen(false)}
        onSaved={() => {
          setSearchOpen(false);
          handleChanged();
        }}
      />
    </Drawer>
  );
};

export default CatalogDetailModal;
